/**
 * Navigation Module
 * helper class
 */
export default class {
  /**
   * constructor
   * @param {Object} options
   */
  constructor(options = {}) {
    this.options = {
      dropdownSelector: '[data-dropdown-id]',
      dropdownTriggerSelector: '[data-trigger-dropdown-id]',
      dropdownTriggerAttribute: 'triggerDropdownId',
      dropdownIconSelector: '[data-dropdown-icon]',
      ...options,
    };

    this.navVisibilityModifier = 'navigation--show';
    this.dropdownVisibilityModifier = 'navigation__dropdown--open';
    this.dropdownIconToggleModifier = 'navigation__dropdown-icon--open';
    this.navLinkActiveModifier = 'navigation__link--active';
    this.navLinkInactiveModifier = 'navigation__link--inactive';

    this.checkbox = document.querySelector('[data-hamburger-input]');
    this.overlay = document.querySelector('[data-mobile-overlay]');
    this.body = document.querySelector('body');
    this.bodyStyle = this.body.style;
    this.scrollPosition = 0;
    this.mql = window.matchMedia('(min-width: 1024px)');
    this.path = window.location.pathname;
    this.links = document.querySelectorAll('[data-link]');

    try {
      //  Chrome & Firefox
      this.mql.addEventListener('change', () => this.breakpointHandler());
    } catch (e1) {
      try {
        //  Safari
        this.mql.addListener(() => this.breakpointHandler());
      } catch (e2) {
        console.error('Safari Issue: could not add EventListener');
      }
    }

    this.checkbox.addEventListener('click', (event) => this.checkboxClickHandler(event));

    this.dropdownTriggers = document.querySelectorAll(this.options.dropdownTriggerSelector);

    this.iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    this.eventType = this.iOS ? 'touchstart' : 'mousedown';

    document.addEventListener('DOMContentLoaded', () => this.handleInactiveState());

    document.body.addEventListener(this.eventType, (event) => this.handleDropdownClick(event));

    this.dropdownTriggers.forEach((el) => {
      el.addEventListener('focus', (event) => this.handleDropdownFocus(event));
    });

    this.links.forEach((el) => {
      el.addEventListener('click', () => this.closeOverlay());
    });
  }

  /**
   * handleDropdownClick
   * @desc opens/closes dropdown menus when clicked
   * @param {Event} event
   */
  handleDropdownClick(event) {
    if (event.target.closest(this.options.dropdownSelector)) {
      event.stopPropagation();
      return;
    }

    this.dropdownTriggers.forEach((trigger) => {
      // open dropdown
      if (event.target === trigger) {
        event.preventDefault();

        document
          .querySelector(`[data-dropdown-id="${trigger.dataset[this.options.dropdownTriggerAttribute]}"]`)
          .classList
          .toggle(this.dropdownVisibilityModifier);

        trigger
          .querySelector(this.options.dropdownIconSelector)
          .classList
          .toggle(this.dropdownIconToggleModifier);

        trigger
          .classList
          .toggle(this.navLinkActiveModifier);

        // close dropdown
      } else if (!('link' in event.target.dataset)) {
        document
          .querySelector(`[data-dropdown-id="${trigger.dataset[this.options.dropdownTriggerAttribute]}"]`)
          .classList
          .remove(this.dropdownVisibilityModifier);

        trigger
          .querySelector(this.options.dropdownIconSelector)
          .classList
          .remove(this.dropdownIconToggleModifier);

        trigger
          .classList
          .remove(this.navLinkActiveModifier);
      }
    });
  }

  /**
   * handleDropdownFocus
   * @desc opens dropdown menus when focusing
   * @param {Event} event
   */
  handleDropdownFocus({ target }) {
    document
      .querySelector(`[data-dropdown-id="${target.dataset[this.options.dropdownTriggerAttribute]}"]`)
      .classList
      .add(this.dropdownVisibilityModifier);

    target
      .querySelector(this.options.dropdownIconSelector)
      .classList
      .add(this.dropdownIconToggleModifier);
  }

  /**
   * enableScrollLock
   * @desc locks the body in place
   *   locks scroll position
   */
  enableScrollLock() {
    this.bodyStyle.overflow = 'hidden';
  }

  /**
   * disableScrollLock
   * @desc restores body scroll
   *   restores scroll position
   */
  disableScrollLock() {
    this.bodyStyle.removeProperty('overflow');
  }

  /**
   * event listener for the mobile header hamburger
   * @desc hide/show overlay
   *   allow/prevent scroll on body
   * @param {ClickEvent} event
   */
  checkboxClickHandler(event) {
    if (this.checkbox.checked) {
      this.overlay.classList.add(this.navVisibilityModifier);
      this.enableScrollLock();
    } else {
      this.overlay.classList.remove(this.navVisibilityModifier);
      this.disableScrollLock();
    }
  }

  /**
   * mql listener for min-width
   * @desc hide overlay
   *   reset checkbox (for css animation)
   *   reset overflow on html tag
   */
  breakpointHandler() {
    if (this.mql.matches) {
      this.closeOverlay();
    }
  }

  /**
   * resets overlay
   * @desc closes overlay
   *   closes overlay
   */
  closeOverlay() {
    this.overlay.classList.remove(this.navVisibilityModifier);
    this.checkbox.checked = false;
    this.disableScrollLock();
  }

  /**
   * sets inactive state in navigation
   * @desc set inactive state
   *   set inactive class
   */
  handleInactiveState() {
    this.links.forEach((link) => {
      if (link.getAttribute('href') === this.path) {
        link.classList.add(this.navLinkInactiveModifier);
      } else {
        link.classList.remove(this.navLinkInactiveModifier);
      }
    });
  }
}
